import { computed } from 'vue';

export function useBlockSafe(rawData) {
  const block = computed(() => rawData || {});

  const id = computed(() => block.value.id || null);
  const columnsCount = computed(() => block.value.columns_count || '');
  const backgroundColor = computed(() => block.value.background_color || '');
  const template = computed(() => block.value.template || '');

  const spacingTop = computed(() =>
    block.value.spacing?.top ? block.value.spacing.top : null,
  );

  const spacingBottom = computed(() =>
    block.value.spacing?.bottom ? block.value.spacing.bottom : null,
  );

  const title = computed(() => block.value.title || '');
  const description = computed(() => block.value.description || '');
  const text = computed(() => block.value.text || '');
  const video = computed(() => block.value.video || '');
  const url = computed(() => block.value.url || '');
  const leftText = computed(() => block.value.left_text || '');
  const rightText = computed(() => block.value.right_text || '');
  const phone = computed(() => block.value.phone || '');
  const email = computed(() => block.value.email || '');
  const address = computed(() => block.value.address || '');

  const hasTitle = computed(() => title.value.length > 0);
  const hasDescription = computed(() => description.value.length > 0);
  const hasText = computed(() => text.value.length > 0);
  const hasVideo = computed(() => video.value.length > 0);
  const hasUrl = computed(() => url.value.length > 0);
  const hasLeftText = computed(() => leftText.value.length > 0);
  const hasRightText = computed(() => rightText.value.length > 0);
  const hasPhone = computed(() => phone.value.length > 0);
  const hasEmail = computed(() => email.value.length > 0);
  const hasAddress = computed(() => address.value.length > 0);

  const image = computed(() =>
    block.value.image && block.value.image.src ? block.value.image : null,
  );
  const hasImage = computed(() => !!image.value);
  const logo = computed(() =>
    block.value.logo && block.value.logo.src ? block.value.logo : null,
  );
  const hasLogo = computed(() => !!logo.value);
  const preview = computed(() =>
    block.value.preview && block.value.preview.src ? block.value.preview : null,
  );
  const redSpot = computed(() =>
    block.value.red_spot &&
    block.value.red_spot.vertical &&
    block.value.red_spot.horizontal
      ? block.value.red_spot
      : null,
  );
  const hasRedSpot = computed(() => !!redSpot.value);

  const items = computed(() =>
    Array.isArray(block.value.items) ? block.value.items : [],
  );
  const hasItems = computed(() => items.value.length > 0);

  const parameters = computed(() =>
    Array.isArray(block.value.parameters) ? block.value.parameters : [],
  );
  const hasParameters = computed(() => parameters.value.length > 0);

  const blocks = computed(() =>
    Array.isArray(block.value.blocks) ? block.value.blocks : [],
  );
  const hasBlocks = computed(() => blocks.value.length > 0);

  const content = computed(() =>
    Array.isArray(block.value.content) ? block.value.content : [],
  );
  const hasContent = computed(() => content.value.length > 0);
  const icons = computed(() =>
    Array.isArray(block.value.icons) ? block.value.icons : [],
  );
  const hasIcons = computed(() => icons.value.length > 0);
  const contacts = computed(() =>
    Array.isArray(block.value.contacts) ? block.value.contacts : [],
  );
  const hasContacts = computed(() => contacts.value.length > 0);

  const button = computed(() =>
    typeof block.value.button === 'object' && block.value.button
      ? block.value.button
      : null,
  );
  const hasButton = computed(() => !!button.value?.url);

  const buttons = computed(() =>
    Array.isArray(block.value.buttons) ? block.value.buttons : [],
  );
  const hasButtons = computed(() => buttons.value.length > 0);
  const services = computed(() =>
    Array.isArray(block.value.services) ? block.value.services : [],
  );
  const hasServices = computed(() => services.value.length > 0);
  const features = computed(() =>
    Array.isArray(block.value.features) ? block.value.features : [],
  );
  const hasFeatures = computed(() => features.value.length > 0);
  const brands = computed(() =>
    Array.isArray(block.value.brands) ? block.value.brands : [],
  );
  const hasBrands = computed(() => brands.value.length > 0);

  const hasAnyContent = computed(
    () =>
      hasTitle.value ||
      hasDescription.value ||
      hasText.value ||
      hasImage.value ||
      hasLogo.value ||
      hasItems.value ||
      hasParameters.value ||
      hasBlocks.value ||
      hasContent.value ||
      hasIcons.value ||
      hasButton.value ||
      hasButtons.value ||
      hasContacts.value ||
      hasUrl.value ||
      hasLeftText.value ||
      hasRightText.value ||
      hasAddress.value ||
      hasEmail.value ||
      hasPhone.value ||
      hasFeatures.value ||
      hasServices.value ||
      hasBrands.value ||
      hasRedSpot.value,
  );

  return {
    block,
    id,
    columnsCount,
    backgroundColor,
    template,
    spacingTop,
    spacingBottom,
    title,
    description,
    text,
    video,
    button,
    image,
    logo,
    preview,
    items,
    parameters,
    blocks,
    icons,
    content,
    buttons,
    contacts,
    url,
    leftText,
    rightText,
    phone,
    email,
    address,
    redSpot,
    features,
    services,
    brands,
    hasTitle,
    hasDescription,
    hasText,
    hasVideo,
    hasButton,
    hasImage,
    hasLogo,
    hasItems,
    hasParameters,
    hasBlocks,
    hasIcons,
    hasButtons,
    hasContacts,
    hasRedSpot,
    hasContent,
    hasUrl,
    hasRightText,
    hasLeftText,
    hasFeatures,
    hasServices,
    hasAnyContent,
    hasAddress,
    hasEmail,
    hasPhone,
    hasBrands,
  };
}
